import React, { useState, useCallback, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { useTitle } from "../../hooks/useTitle";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	Box,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	HelpOutline
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import TwilioConnectModal from "../../components/TwilioConnectModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import { isWhatsAppInitializingStatus } from "../../helpers/whatsAppState";
import TwilioIcon from "../../components/Icons/TwilioIcon";
import TwilioInstructionsModal from "../../components/TwilioInstructionsModal";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
	tooltipFont: {
    fontSize: "0.9rem",
  },
}));

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const Connections = () => {
	useTitle("Conexiones");
	const classes = useStyles();

	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const { user } = useContext(AuthContext);
	const isAdmin = user && (user.profile === "admin" || user.profile === "superadmin");
	const [maxConnections, setMaxConnections] = useState(1); 
	const hasConnections = whatsApps && whatsApps.length > 0;
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [twilioModalOpen, setTwilioModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const twilioSids = whatsApps
		.filter(conn => conn.connectionType === "twilio" && conn.twilioAccountSid)
		.map(conn => conn.twilioAccountSid);
	
	const [instructionsOpen, setInstructionsOpen] = useState(false);

	const handleOpenInstructions = () => {
		setInstructionsOpen(true);
	};

	const handleCloseInstructions = () => {
		setInstructionsOpen(false);
	};

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
		try {
			await api.put(`/whatsappsession/${whatsApp.id}`);
		} catch (err) {
			setQrModalOpen(false);
			setSelectedWhatsApp(null);
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleOpenTwilioModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setTwilioModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleCloseTwilioModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setTwilioModalOpen(false);
	}, [setTwilioModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	useEffect(() => {
    const fetchSettings = async () => {
      if (!user?.companyId) return;
      try {
        const { data } = await api.get("/company-settings", {
          params: { companyId: user.companyId }
        });
        
        const responseData = Array.isArray(data) ? data : (data.data || []);
        const myCompanyData = responseData[0];

        if (myCompanyData && myCompanyData.setting) {
          setMaxConnections(myCompanyData.setting.maxConnections || 3);
        }
      } catch (err) {
        console.error("Error fetching company settings", err);
      }
    };
    fetchSettings();
  }, [user]);

	const currentConnections = whatsApps ? whatsApps.length : 0;
  const isLimitReached = currentConnections >= maxConnections;

	const addButtonTooltip = isLimitReached 
    ? (isAdmin 
        ? "Límite de " + maxConnections + " conexiones alcanzado. Contacta a administración para ampliar la cantidad." 
        : "Límite de conexiones alcanzado.")
    : "Agregar nueva conexión de WhatsApp";

	const renderActionButtons = whatsApp => {
		return (
			<>
				{whatsApp.status === "qrcode" && (
					<>
						<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
						>
						{i18n.t("connections.buttons.qrcode")}
						</Button>

						<span style={{ marginRight: 8 }} />
	
						<Button
						size="small"
						variant="contained"
						color="secondary"
						onClick={() => handleOpenTwilioModal(whatsApp)}
						startIcon={<TwilioIcon style={{ color: 'white', width: 18, height: 18 }} />}
						>
						{i18n.t("connections.buttons.twilio")}
						</Button>
					</>
				)}
				{whatsApp.status === "DISCONNECTED" && (
					<>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>{" "}
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRequestNewQrCode(whatsApp)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{(whatsApp.status === "CONNECTED" ||
					whatsApp.status === "PAIRING" ||
					whatsApp.status === "TIMEOUT") && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => {
							handleOpenConfirmationModal("disconnect", whatsApp.id);
						}}
					>
						{whatsApp.connectionType === "twilio"
						? i18n.t("connections.buttons.disconnectTwilio") + whatsApp.twilioPhoneNumber
						: i18n.t("connections.buttons.disconnectQR")}
					</Button>
				)}
				{isWhatsAppInitializingStatus(whatsApp.status) && (
					<Button size="small" variant="outlined" disabled color="default">
						{whatsApp.status === "AUTHENTICATED"
							? i18n.t("connections.buttons.finalizing")
							: i18n.t("connections.buttons.connecting")}
					</Button>
				)}
			</>
		);
	};

	const renderStatusToolTips = whatsApp => {
		return (
			<div className={classes.customTableCell}>
				{whatsApp.status === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "OPENING" && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}
				{whatsApp.status === "AUTHENTICATED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.authenticated.title")}
						content={i18n.t("connections.toolTips.authenticated.content")}
					>
						<CircularProgress size={24} className={classes.buttonProgress} />
					</CustomToolTip>
				)}
				{whatsApp.status === "qrcode" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{whatsApp.status === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	return (
		<MainContainer>
			<TwilioInstructionsModal
				open={instructionsOpen}
				onClose={handleCloseInstructions}
				accountSids={twilioSids}
			/>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<TwilioConnectModal
				open={twilioModalOpen}
				onClose={handleCloseTwilioModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
				isFirstConnection={!hasConnections}
			/>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mb={1}>
						<CustomToolTip
							title="Instrucciones para conectar con Twilio"
							content="Configura tu cuenta Twilio para que los mensajes entrantes lleguen correctamente a la plataforma. Haz clic para ver los pasos detallados."
						>
							<IconButton onClick={handleOpenInstructions}>
							<HelpOutline />
							</IconButton>
						</CustomToolTip>
	
						<Tooltip title={addButtonTooltip} arrow placement="bottom" classes={{ tooltip: classes.tooltipFont }}>
							<span>
								<Button
									variant="contained"
									color="primary"
									onClick={handleOpenWhatsAppModal}
									disabled={isLimitReached}
								>
									{i18n.t("connections.buttons.add")}
								</Button>
							</span>
						</Tooltip>
	
						{isAdmin && (
 							<Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
								{currentConnections} / {maxConnections}
 							</Typography>
 						)}
					</Box>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
              <TableCell align="center">
								id
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.session")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.default")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : (
							<>
								{whatsApps?.length > 0 &&
									whatsApps.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">{whatsApp.id}</TableCell>
											<TableCell align="center">{whatsApp.name}</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{renderActionButtons(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
											</TableCell>
											<TableCell align="center">
												{whatsApp.isDefault && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell align="center">
												<IconButton
													size="small"
													onClick={() => handleEditWhatsApp(whatsApp)}
												>
													<Edit />
												</IconButton>

												<IconButton
													size="small"
													onClick={e => {
														handleOpenConfirmationModal("delete", whatsApp.id);
													}}
												>
													<DeleteOutline />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Connections;
